import dayjs, { Dayjs } from "dayjs";
import { AdminLeadStatus, Category, TableFilter } from "../../../../../api";
import { CategoryMapping } from "../../shared/Constants";
import { HelpersService } from "../../../../../services/Helpers";
import {
  DRAWER_FILTER_FIELDS,
  EMPTY_LEADS_FILTERS,
  terminationAgeLabel,
  type LeadsFiltersState,
} from "./types";

export function countActiveFilters(filters: LeadsFiltersState): number {
  let count = 0;
  if (filters.category.trim()) count += 1;
  if (filters.stage.trim()) count += 1;
  if (filters.createdById.trim()) count += 1;
  if (filters.reviewingAttorneyId.trim()) count += 1;
  if (filters.createdFrom != null) count += 1;
  if (filters.createdTo != null) count += 1;
  if (filters.terminationAge.trim()) count += 1;
  return count;
}

export function hasActiveFilters(filters: LeadsFiltersState): boolean {
  return countActiveFilters(filters) > 0;
}

export function clearFilterField(
  filters: LeadsFiltersState,
  key: keyof LeadsFiltersState
): LeadsFiltersState {
  if (key === "createdFrom" || key === "createdTo") {
    return { ...filters, [key]: null };
  }
  if (key === "createdById") {
    return { ...filters, createdById: "", createdByName: "" };
  }
  if (key === "reviewingAttorneyId") {
    return { ...filters, reviewingAttorneyId: "", reviewingAttorneyName: "" };
  }
  return { ...filters, [key]: "" };
}

export function resetFilters(): LeadsFiltersState {
  return { ...EMPTY_LEADS_FILTERS };
}

export function formatDateParam(value: Dayjs | null): string | null {
  return value ? dayjs(value).format("YYYY-MM-DD") : null;
}

export function toTableFilters(filters: LeadsFiltersState): TableFilter[] {
  const result: TableFilter[] = [];

  if (filters.category) {
    result.push({
      filterField: "Category",
      filterValue: filters.category,
      isEnum: true,
    });
  }
  if (filters.stage) {
    result.push({
      filterField: "Status",
      filterValue: filters.stage,
      isEnum: true,
    });
  }
  if (filters.createdById) {
    result.push({
      filterField: "CreatedBy",
      filterValue: filters.createdById,
      isEnum: false,
    });
  }
  if (filters.reviewingAttorneyId) {
    result.push({
      filterField: "ReviewingAttorney",
      filterValue: filters.reviewingAttorneyId,
      isEnum: false,
    });
  }
  const createdFrom = formatDateParam(filters.createdFrom);
  if (createdFrom) {
    result.push({
      filterField: "CreatedFrom",
      filterValue: createdFrom,
      isEnum: false,
    });
  }
  const createdTo = formatDateParam(filters.createdTo);
  if (createdTo) {
    result.push({
      filterField: "CreatedTo",
      filterValue: createdTo,
      isEnum: false,
    });
  }
  if (filters.terminationAge) {
    result.push({
      filterField: "TerminationAge",
      filterValue: filters.terminationAge,
      isEnum: false,
    });
  }

  return result;
}

function mapLegacyTerminationValue(value: string): string {
  const normalized = value.trim().toLowerCase();
  if (normalized === "true" || normalized === "yes") {
    return "lte:12";
  }
  if (normalized === "false" || normalized === "no") {
    return "gt:12";
  }
  return value;
}

type NamedUser = {
  id?: number;
  firstName?: string | null;
  lastName?: string | null;
};

export function filtersFromTableFilters(
  tableFilters: TableFilter[] | null | undefined,
  creators: NamedUser[] = [],
  reviewingAttorneys: NamedUser[] = []
): LeadsFiltersState {
  const state = resetFilters();
  if (!tableFilters?.length) {
    return state;
  }

  for (const filter of tableFilters) {
    const field = filter.filterField;
    const value = filter.filterValue ?? "";
    if (field === "Category") {
      state.category = value;
    } else if (field === "Status") {
      state.stage = value;
    } else if (field === "CreatedBy") {
      state.createdById = value;
      const creator = creators.find((c) => String(c.id) === value);
      state.createdByName = creator
        ? `${creator.firstName ?? ""} ${creator.lastName ?? ""}`.trim()
        : value;
    } else if (field === "ReviewingAttorney") {
      state.reviewingAttorneyId = value;
      const attorney = reviewingAttorneys.find((a) => String(a.id) === value);
      state.reviewingAttorneyName = attorney
        ? `${attorney.firstName ?? ""} ${attorney.lastName ?? ""}`.trim()
        : value;
    } else if (field === "CreatedFrom" && value) {
      const parsed = dayjs(value);
      state.createdFrom = parsed.isValid() ? parsed : null;
    } else if (field === "CreatedTo" && value) {
      const parsed = dayjs(value);
      state.createdTo = parsed.isValid() ? parsed : null;
    } else if (field === "TerminationAge" && value) {
      state.terminationAge = value;
    } else if (field === "TerminationWithinYear" && value) {
      state.terminationAge = mapLegacyTerminationValue(value);
    }
  }

  return state;
}

export function mergeDrawerFilters(
  existing: TableFilter[] | null | undefined,
  drawerFilters: LeadsFiltersState
): TableFilter[] {
  const preserved = (existing ?? []).filter(
    (f) =>
      !DRAWER_FILTER_FIELDS.includes(
        f.filterField as (typeof DRAWER_FILTER_FIELDS)[number]
      )
  );
  return [...preserved, ...toTableFilters(drawerFilters)];
}

export function getCategoryOptions(): { value: string; label: string }[] {
  return Object.values(Category).map((value) => ({
    value,
    label: CategoryMapping[value] ?? value,
  }));
}

export function getStageOptions(): { value: string; label: string }[] {
  return Object.values(AdminLeadStatus).map((value) => ({
    value,
    label: HelpersService.camelToString(value, false) || value,
  }));
}

export function buildActiveChips(filters: LeadsFiltersState): {
  key: keyof LeadsFiltersState;
  label: string;
}[] {
  const chips: { key: keyof LeadsFiltersState; label: string }[] = [];

  if (filters.category) {
    chips.push({
      key: "category",
      label: `Category: ${CategoryMapping[filters.category as Category] ?? filters.category}`,
    });
  }
  if (filters.stage) {
    chips.push({
      key: "stage",
      label: `Stage: ${HelpersService.camelToString(filters.stage, false) || filters.stage}`,
    });
  }
  if (filters.createdById) {
    chips.push({
      key: "createdById",
      label: `Created by: ${filters.createdByName || filters.createdById}`,
    });
  }
  if (filters.reviewingAttorneyId) {
    chips.push({
      key: "reviewingAttorneyId",
      label: `Reviewing Attorney: ${filters.reviewingAttorneyName || filters.reviewingAttorneyId}`,
    });
  }
  if (filters.createdFrom) {
    chips.push({
      key: "createdFrom",
      label: `Created At from: ${dayjs(filters.createdFrom).format("MM/DD/YYYY")}`,
    });
  }
  if (filters.createdTo) {
    chips.push({
      key: "createdTo",
      label: `Created At to: ${dayjs(filters.createdTo).format("MM/DD/YYYY")}`,
    });
  }
  if (filters.terminationAge) {
    chips.push({
      key: "terminationAge",
      label: `Termination: ${terminationAgeLabel(filters.terminationAge)}`,
    });
  }

  return chips;
}
